"use client";

import { useIncomingLetterAudienceMutation } from "@/components/parts/mail/incoming-mail/audience/api";
import {
  AudiencePayload,
  AudienceValidation,
} from "@/components/parts/mail/incoming-mail/audience/validation";
import { generateDynamicFormIncomingLetterPhysicalSchema } from "@/components/parts/mail/incoming-mail/phisical/validation";
import { useGetTempelateLetterDetailByTypeId } from "@/components/parts/template/letter/api";
import { CustomFormCalender } from "@/components/shared/forms/customFormCalender";
import { CustomFormDragAndDrop } from "@/components/shared/forms/customFormDragAndDrop";
import {
  CustomFormInput,
  inputFilters,
} from "@/components/shared/forms/customFormInput";
import { CustomFormTextArea } from "@/components/shared/forms/customFormTextArea";
import CustomFormTime from "@/components/shared/forms/customFormTime";
import CustomFromRegions from "@/components/shared/forms/customFromRegions";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { zodResolver } from "@hookform/resolvers/zod";
import { Info, Loader } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

export default function Page() {
  const router = useRouter();
  const baseSchema = useMemo(() => AudienceValidation, []);
  const [validationSchema, setValidationSchema] = useState(baseSchema);

  const form = useForm<AudiencePayload>({
    resolver: zodResolver(validationSchema),
  });

  const {
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;

  const { data: _templateData } =
    useGetTempelateLetterDetailByTypeId(`surat-audiensi`);
  const TemplateData = _templateData?.data.letterSections;

  const incomingLetterAudienceMutation = useIncomingLetterAudienceMutation();

  const onSubmit = handleSubmit((value) => {
    console.log(value);
    incomingLetterAudienceMutation.mutate(value, {
      onSuccess: () => {
        reset();
        router.back();
      },
    });
  });

  useEffect(() => {
    if (TemplateData) {
      const { schema } =
        generateDynamicFormIncomingLetterPhysicalSchema(TemplateData);
      const newSchema = baseSchema.merge(schema) as any;
      setValidationSchema(newSchema);
    }
  }, [TemplateData, baseSchema]);

  return (
    <main className="container md:w-2/3 z-20 mt-9 mb-16">
      <Card>
        <CardContent className="p-5">
          <CardTitle className="text-primary text-2xl">
            Form Permohonan Audiensi
          </CardTitle>
          <Separator className="border-b-2 mt-5" />
          <Alert
            variant="default"
            className="mt-4 bg-info-100 border-none space-x-2 place-content-center"
          >
            <Info className="!text-info-500" />
            <AlertTitle className="text-info-500 text-base md:text-lg">
              Info
            </AlertTitle>
            <AlertDescription className="text-xs md:text-sm">
              Silakan lengkapi data pemohon, informasi instansi serta informasi
              tujuan audiensi. Unggah surat resmi pendukung pada tab Upload.
              Audiensi akan diproses 1–3 hari kerja. Data Anda hanya digunakan
              untuk keperluan audiensi DPRD Kabupaten PALI
            </AlertDescription>
          </Alert>
          {/*  */}
          <form onSubmit={onSubmit}>
            <Form {...form}>
              <div className="grid grid-cols-1 gap-y-6 mt-6">
                {/* <GenerateHeader /> */}
                <div className="grid grid-cols-1 gap-6">
                  <p className="text-primary-800 font-semibold text-xl">
                    Pemohon/Penanggung Jawab
                  </p>
                  <CustomFormInput<AudiencePayload>
                    name="name"
                    label="Nama"
                    placeholder="Nama"
                    required
                  />
                  <CustomFormInput<AudiencePayload>
                    name="phone"
                    label="No. HP"
                    placeholder="No. HP"
                    filterInput={inputFilters.alphanumeric}
                    mask="62 ____-____-_____"
                    required
                  />
                  <CustomFormInput<AudiencePayload>
                    name="email"
                    label="Email"
                    placeholder="Email"
                    filterInput={inputFilters.email}
                    required
                  />
                </div>
                <div className="grid grid-cols-1 gap-6">
                  <p className="text-primary-800 font-semibold text-xl">
                    Informasi Instansi Tamu
                  </p>
                  <CustomFormInput<AudiencePayload>
                    name="originInstansi"
                    label="Asal Instansi"
                    placeholder="Asal Instansi"
                    required
                  />
                  <CustomFromRegions<AudiencePayload>
                    region="provinces"
                    name="provinceCode"
                    label="Provinsi"
                    placeholder="Provinsi"
                    required
                  />
                  <CustomFromRegions<AudiencePayload>
                    code={watch("provinceCode")}
                    region="cities"
                    name="cityCode"
                    label="Kabupaten/ Kota"
                    placeholder="Kabupaten/ Kota"
                    required
                  />
                  <CustomFormTextArea<AudiencePayload>
                    name="address"
                    label="Alamat Instansi"
                    placeholder="Alamat Instansi"
                    required
                  />
                </div>
                <div className="grid grid-cols-1 gap-6">
                  <p className="text-primary-800 font-semibold text-xl">
                    Informasi Topik/Tujuan Audiensi
                  </p>
                  <CustomFormInput<AudiencePayload>
                    name="purpose"
                    label="Tujuan Audiensi"
                    placeholder="Tujuan Audiensi"
                    required
                  />
                  <CustomFormCalender<AudiencePayload>
                    name="audienceDate"
                    label="Tanggal Audiensi"
                    placeholder="Tanggal Audiensi"
                    required
                  />
                  <CustomFormTime<AudiencePayload>
                    name="audienceTime"
                    label="Jam Audiensi"
                    placeholder="Jam Audiensi"
                    required
                  />
                  <CustomFormDragAndDrop<AudiencePayload>
                    name="audienceLetter"
                    label="Upload Surat Permohonan Audiensi"
                    acceptedFileTypes={["application/pdf"]}
                    required
                  />
                </div>
              </div>
              {/* <Separator className="mt-8" />
              <p className="text-primary-800 font-semibold text-xl my-5">
                Informasi Tambahan
              </p>
              <FormDynamicAudience /> */}
              <div className="flex gap-x-4 justify-end mt-5">
                <Button
                  type="submit"
                  className="rounded-full min-w-32"
                  disabled={incomingLetterAudienceMutation.isPending}
                >
                  {incomingLetterAudienceMutation.isPending ? (
                    <Loader className="animate-spin" />
                  ) : (
                    "Kirim Permohonan"
                  )}
                </Button>
              </div>
            </Form>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
